import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Anchor, Button, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import AuthCard from '@/components/auth/AuthCard';
import { userManagementService } from '@/api/services/UserManagementService';
import { isNetworkError } from '@/api/errors';
import { useCooldown } from '@/hooks/useCooldown';
import { EMAIL_PATTERN, isBlank } from '@/lib/validators';
import { ROUTES } from '@/routes/paths';

interface ForgotValues {
  email: string;
}

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [sentEmail, setSentEmail] = useState<string | null>(null);
  const resend = useCooldown(30);

  const form = useForm<ForgotValues>({
    mode: 'uncontrolled',
    initialValues: { email: '' },
    validate: {
      email: (value) =>
        isBlank(value)
          ? t('auth.validation.required')
          : EMAIL_PATTERN.test(value.trim())
            ? null
            : t('auth.validation.email'),
    },
    validateInputOnBlur: true,
  });

  // Нейтральная обработка: успех показываем при любом ответе, кроме сетевой ошибки —
  // чтобы не раскрывать, зарегистрирован ли email (см. решение по приватности).
  async function submitReset(email: string): Promise<boolean> {
    try {
      await userManagementService.requestPasswordReset(email);
    } catch (error) {
      if (isNetworkError(error)) {
        notifications.show({ color: 'red', message: t('auth.errors.generic') });
        return false;
      }
    }
    return true;
  }

  async function handleSubmit(values: ForgotValues): Promise<void> {
    const email = values.email.trim();
    setSubmitting(true);
    const ok = await submitReset(email);
    setSubmitting(false);
    if (ok) {
      setSentEmail(email);
    }
  }

  async function handleResend(): Promise<void> {
    if (!sentEmail) {
      return;
    }
    const ok = await submitReset(sentEmail);
    if (ok) {
      resend.start();
      notifications.show({ color: 'lime', message: t('auth.forgotPassword.resent') });
    }
  }

  if (sentEmail) {
    return (
      <AuthCard
        title={t('auth.forgotPassword.successTitle')}
        description={t('auth.forgotPassword.successText')}
        footer={
          <Anchor component={Link} to={ROUTES.LOGIN} size="sm" c="brand.7">
            {t('auth.forgotPassword.backToLogin')}
          </Anchor>
        }
      >
        <Button variant="subtle" w="fit-content" disabled={resend.active} onClick={handleResend}>
          {resend.active
            ? t('auth.forgotPassword.resendRetry', { seconds: resend.remaining })
            : t('auth.forgotPassword.resend')}
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={t('auth.forgotPassword.title')}
      description={t('auth.forgotPassword.text')}
      footer={
        <Text size="sm">
          <Anchor component={Link} to={ROUTES.LOGIN} c="brand.7">
            {t('auth.forgotPassword.cancel')}
          </Anchor>
        </Text>
      }
    >
      <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
        <Stack gap="md">
          <TextInput
            label={t('auth.fields.email')}
            type="email"
            autoComplete="email"
            withAsterisk
            key={form.key('email')}
            {...form.getInputProps('email')}
          />
          <Button type="submit" loading={submitting} fullWidth mt="xs">
            {t('auth.forgotPassword.submit')}
          </Button>
        </Stack>
      </form>
    </AuthCard>
  );
}
